import {typeboxResolver} from '@hookform/resolvers/typebox'
import {announce} from '@react-aria/live-announcer'
import {type Static, Type} from '@sinclair/typebox'
import {TypeCompiler} from '@sinclair/typebox/compiler'
import {Value} from '@sinclair/typebox/value'
import {Button, FieldError, Form, Label, Text, TextField} from 'react-aria-components'
import {Controller, type SubmitHandler, useForm} from 'react-hook-form'

import PasswordInput from '@/components/PasswordInput/PasswordInput'
import {SignInRoute} from '@/routeRegistry'
import {resetPassword} from '@/services/authenticationService'

import style from './ResetPasswordStep2.module.scss'

const schema = Type.Object({
  password: Type.String({
    default: '',
    minLength: 8,
    format: 'password',
    errorMessage:
      'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter & one number',
  }),
  confirmPassword: Type.String({
    default: '',
    minLength: 1,
  }),
})

const typecheck = TypeCompiler.Compile(schema)

type Schema = Static<typeof schema>

interface Props {
  token: string
}

export default memo(function ResetPasswordStep2({token}: Props) {
  const navigate = useNavigate()

  const [isResetting, setIsResetting] = useState(false)

  const {handleSubmit, control, setError} = useForm<Schema>({
    mode: 'onTouched',
    resolver: typeboxResolver(typecheck),
    defaultValues: Value.Create(schema),
  })

  const onSubmit: SubmitHandler<Schema> = data => {
    if (data.password !== data.confirmPassword) {
      setError('confirmPassword', {message: 'Passwords do not match'})
      return
    }
    setIsResetting(true)
    void resetPassword({
      token,
      password: data.password,
    })
      .then(result => {
        if (result) {
          if (typeof result === 'string') {
            announce(result, 'assertive')
            setError(
              'password',
              {message: result},
              {
                shouldFocus: true,
              },
            )
          } else {
            void navigate({
              to: SignInRoute.fullPath,
            })
          }
        }
      })
      .finally(() => {
        setIsResetting(false)
      })
  }

  return (
    <Form className={style.Form} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name='password'
        render={({field: {name, value, onChange, onBlur, ref}, fieldState: {invalid, error}}) => (
          <TextField
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            validationBehavior='aria'
            isInvalid={invalid}
            isRequired
            type='password'
            className={style.Field}
          >
            <Label className={style.Label}>New password</Label>
            <PasswordInput ref={ref} className={clsx(style.Input, style.InputGroup)} />
            {error?.message ? (
              <FieldError className={style.Error}>{error.message}</FieldError>
            ) : (
              <Text slot='description' className={style.Description}>
                Use 8 characters or more with a mix of uppercase letters, lowercase letters, and
                numbers
              </Text>
            )}
          </TextField>
        )}
      />
      <Controller
        control={control}
        name='confirmPassword'
        render={({field: {name, value, onChange, onBlur, ref}, fieldState: {invalid, error}}) => (
          <TextField
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            validationBehavior='aria'
            isInvalid={invalid}
            isRequired
            type='password'
            className={style.Field}
          >
            <Label className={style.Label}>Confirm password</Label>
            <PasswordInput ref={ref} className={clsx(style.Input, style.InputGroup)} />

            {!!error?.message && <FieldError className={style.Error}>{error.message}</FieldError>}
          </TextField>
        )}
      />
      <div className={style.SubmitArea}>
        <Button className={style.SignUpBtn} type='submit' isDisabled={isResetting}>
          {!isResetting ? 'Save and Sign in' : 'Saving...'}
        </Button>
      </div>
    </Form>
  )
})
